import type { WithID } from '@tunasong/models'
import type { CalendarEvent } from '@tunasong/schemas'
import dayjs from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'
import type { ICalDateTimeValue, ICalEventData } from 'ical-generator'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

/**
 * A date-time for ical-generator. With a timezone it must be a date-library object: given a string or a Date it
 * formats the TZID'd local time with the *process* timezone (`getHours()`), which is UTC in a Lambda, so 19:00 in Oslo
 * came out as 17:00 labelled Oslo. A dayjs object with the timezone plugin is converted to the event's zone instead.
 */
const icalDate = (iso: string, timezone: string | undefined): ICalDateTimeValue => (timezone ? dayjs.utc(iso) : iso)

/** RFC 5545 UTC date-time: 20261022T170000Z */
const icalUtc = (iso: string) =>
  new Date(iso)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')

/** RFC 5545 local date-time in a timezone: 20261022T190000 */
const icalLocal = (iso: string, timezone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(iso))
  const field = (type: string) => parts.find(part => part.type === type)?.value ?? '00'
  return `${field('year')}${field('month')}${field('day')}T${field('hour')}${field('minute')}${field('second')}`
}

/**
 * The RRULE and one EXDATE per cancelled occurrence (spec R5), as lines inserted into the VEVENT. With a timezone the
 * EXDATE is written like DTSTART, in local time with its TZID: some clients match only that form.
 */
const repeatingLines = ({ recurrence, exceptions = {}, timezone }: CalendarEvent) => {
  if (!recurrence) {
    return null
  }
  const cancelled = Object.entries(exceptions)
    .filter(([, exception]) => exception.cancelled)
    .map(([originalStart]) => originalStart)
    .sort((a, b) => Date.parse(a) - Date.parse(b))
  const exdate = timezone
    ? `EXDATE;TZID=${timezone}:${cancelled.map(start => icalLocal(start, timezone)).join(',')}`
    : `EXDATE:${cancelled.map(icalUtc).join(',')}`
  return [`RRULE:${recurrence}`, ...(cancelled.length > 0 ? [exdate] : [])].join('\n')
}

const baseEvent = (event: WithID<CalendarEvent>): ICalEventData => {
  const {
    id,
    name = 'Untitled',
    start = null,
    end = null,
    allDay,
    description,
    location,
    organizer,
    attendees = [],
  } = event
  return {
    id,
    start: start ? icalDate(start, event.timezone) : null,
    end: end ? icalDate(end, event.timezone) : null,
    description: description ? { plain: description } : null,
    /** Revision number: increases with every change (R5) */
    sequence: event.sequence ?? 0,
    recurrenceId: null,
    timezone: event.timezone ?? null,
    stamp: new Date().toISOString(),
    allDay,
    floating: false,
    repeating: null,
    summary: name,
    location: location ?? null,
    organizer: organizer?.email,
    /** An attendee without a known email cannot be invited */
    attendees: attendees.filter(a => a.email).map(a => ({ email: a.email })),
    alarms: [],
    categories: [],
    status: null,
    busystatus: null,
    priority: null,
    url: null,
    attachments: [],
    transparency: null,
    created: null,
    lastModified: null,
    x: [],
  }
}

/**
 * An event as iCal VEVENTs (GH #2814, spec R5): the series with its RRULE and EXDATEs, then one overriding VEVENT per
 * moved or edited occurrence, carrying the series' UID and the occurrence's original start as RECURRENCE-ID.
 */
export const toICalEvents = (event: WithID<CalendarEvent>): ICalEventData[] => {
  const series = { ...baseEvent(event), repeating: repeatingLines(event) }
  if (!(event.recurrence && event.start)) {
    return [series]
  }
  const duration = event.end ? Date.parse(event.end) - Date.parse(event.start) : undefined
  const overrides = Object.entries(event.exceptions ?? {})
    .filter(([, exception]) => !exception.cancelled)
    .sort(([a], [b]) => Date.parse(a) - Date.parse(b))
    .map(([originalStart, exception]): ICalEventData => {
      const start = exception.start ?? originalStart
      const end =
        exception.end ?? (duration === undefined ? null : new Date(Date.parse(start) + duration).toISOString())
      return {
        ...baseEvent(event),
        recurrenceId: icalDate(originalStart, event.timezone),
        start: icalDate(start, event.timezone),
        end: end ? icalDate(end, event.timezone) : null,
        summary: exception.name ?? event.name ?? 'Untitled',
        location: exception.location ?? event.location ?? null,
      }
    })
  return [series, ...overrides]
}
